import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ExternalLink, Upload, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { corSituacao, motivoAgora, podeImportar, resumoLista, rotuloSituacao } from './listaLL';
import { corPrioridade, diasAberto } from './listaCSU';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Lista CSU (Adnéia, 03/11/2026), como a Lista LL: o analista importa a lista dos chamados que a CSU
// transferiu à WeConnect; o ELO acha ou abre o atendimento de cada um (routes/lista_csu.py). Aqui ele
// vê a lista pela prioridade da CSU, abre cada atendimento numa aba nova e marca "Conferido".
export default function ListaCSUModal({ onClose, getAuthHeader, user }) {
  const [lista, setLista] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [importando, setImportando] = useState(false);
  const [soPendentes, setSoPendentes] = useState(false);
  const arquivoRef = useRef(null);
  // O getAuthHeader do AuthContext muda a cada render: pelo ref, a lista carrega só ao abrir.
  const authRef = useRef(getAuthHeader);
  authRef.current = getAuthHeader;

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/lista-csu`, { headers: authRef.current() });
      setLista({ ...data, tickets: data.chamados || [] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao carregar a Lista CSU');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const importar = async (ev) => {
    const arquivo = ev.target.files && ev.target.files[0];
    ev.target.value = '';
    if (!arquivo) return;
    setImportando(true);
    try {
      const corpo = new FormData();
      corpo.append('file', arquivo);
      const { data } = await axios.post(`${API_URL}/api/lista-csu/importar`, corpo, { headers: authRef.current() });
      const r = data.resumo || {};
      const abertos = (r.aberto || 0) + (r.aberto_sem_entrega || 0);
      const jaTinham = (r.ja_tinha || 0) + (r.ja_tinha_entrega || 0);
      toast.success(`Lista CSU importada: ${abertos} atendimento(s) aberto(s), ${jaTinham} já tinham`
        + (r.nao_abriu ? `, ${r.nao_abriu} não abriram` : ''));
      await carregar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao importar o arquivo');
    } finally {
      setImportando(false);
    }
  };

  const marcar = async (t, valor) => {
    try {
      await axios.put(`${API_URL}/api/lista-csu/${lista.data}/${t.chamado}/conferido`, { conferido: valor },
        { headers: authRef.current() });
      setLista(l => ({
        ...l,
        tickets: l.tickets.map(x => (x.chamado === t.chamado
          ? { ...x, conferido: valor, conferido_por: valor ? (user?.name || '') : '' } : x)),
      }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao marcar');
    }
  };

  const abrirAtendimento = (t) => {
    if (t.chamado_id) window.open(`/chamados/editar/${t.chamado_id}`, '_blank', 'noopener');
  };

  const chamados = (lista?.tickets || []).filter(t => !soPendentes || !t.conferido);
  const r = resumoLista(lista?.tickets);
  const dia = lista?.data ? lista.data.split('-').reverse().join('/') : '';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-6xl mx-4 max-h-[85vh] flex flex-col"
        onClick={e => e.stopPropagation()} data-testid="modal-lista-csu">
        <div className="flex items-start justify-between gap-3 p-4 border-b">
          <div>
            <h2 className="font-bold text-base">📋 Lista CSU {dia && `· ${dia}`}</h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Chamados que a CSU transferiu à WeConnect, pela prioridade (Crítico primeiro) e, dentro dela, do mais antigo.
              {lista?.importada && ` Importada por ${lista.importado_por || '?'} às ${lista.importado_em_brt || '?'}.`}
            </p>
            {lista?.importada && (
              <p className="text-xs mt-1">
                <b>{r.total}</b> chamados · <b>{r.conferidos}</b> conferidos · {r.abertos} abertos agora
                {r.sem_entrega > 0 && ` (${r.sem_entrega} sem entrega)`} · {r.ja_tinham} já tinham atendimento
                {r.nao_abriu > 0 && <span className="text-red-700 dark:text-red-400"> · {r.nao_abriu} não abriram</span>}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {lista?.importada && (
              <label className="flex items-center gap-1.5 text-xs cursor-pointer select-none">
                <Checkbox checked={soPendentes} onCheckedChange={v => setSoPendentes(!!v)} />
                Só os não conferidos
              </label>
            )}
            {podeImportar(lista, user) && (
              <>
                <input ref={arquivoRef} type="file" accept=".xlsx,.csv" className="hidden" onChange={importar}
                  data-testid="input-lista-csu" />
                <Button size="sm" variant="outline" disabled={importando || carregando}
                  onClick={() => arquivoRef.current && arquivoRef.current.click()}>
                  <Upload className="h-4 w-4 mr-1" />
                  {importando ? 'Importando...' : lista?.importada ? 'Importar de novo (admin)' : 'Importar lista da CSU'}
                </Button>
              </>
            )}
            <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
          </div>
        </div>
        <div className="overflow-auto flex-1">
          {carregando ? (
            <p className="p-6 text-sm text-muted-foreground">Carregando...</p>
          ) : !lista?.importada ? (
            <p className="p-6 text-sm text-muted-foreground">
              A lista de hoje ainda não foi importada. Clique em "Importar lista da CSU" e escolha a planilha dos
              chamados da CSU (.xlsx). O ELO abre o atendimento de quem ainda não tem, em "Ag. Conferência CSU".
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-slate-50 dark:bg-slate-800 border-b">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-slate-600">Prioridade</th>
                  <th className="px-3 py-2 text-left font-medium text-slate-600">Chamado</th>
                  <th className="px-3 py-2 text-left font-medium text-slate-600">Aberto na CSU</th>
                  <th className="px-3 py-2 text-left font-medium text-slate-600">Atendimento</th>
                  <th className="px-3 py-2 text-left font-medium text-slate-600">Entrega</th>
                  <th className="px-3 py-2 text-left font-medium text-slate-600">Motivo agora</th>
                  <th className="px-3 py-2 text-left font-medium text-slate-600">Na importação</th>
                  <th className="px-3 py-2 text-left font-medium text-slate-600">Banco · Fila</th>
                  <th className="px-3 py-2 text-center font-medium text-slate-600">Conferido</th>
                </tr>
              </thead>
              <tbody>
                {chamados.map(t => {
                  const dias = diasAberto(t.aberto_em);
                  return (
                    <tr key={t.chamado} className={`border-b hover:bg-slate-50 dark:hover:bg-slate-800/30 ${t.conferido ? 'opacity-50' : ''}`}>
                      <td className={`px-3 py-1.5 whitespace-nowrap text-xs ${corPrioridade(t.prioridade)}`}>{t.prioridade || '?'}</td>
                      <td className="px-3 py-1.5 font-mono text-xs">{t.chamado}</td>
                      <td className="px-3 py-1.5 whitespace-nowrap text-xs">
                        {t.aberto_em || '?'}{dias != null && <span className="text-muted-foreground"> · há {dias} dia(s)</span>}
                      </td>
                      <td className="px-3 py-1.5 whitespace-nowrap">
                        {t.chamado_id ? (
                          <button onClick={() => abrirAtendimento(t)} className="text-blue-700 dark:text-blue-400 hover:underline inline-flex items-center gap-1 font-mono text-xs">
                            {t.id_atendimento} <ExternalLink className="h-3 w-3" />
                          </button>
                        ) : <span className="text-xs text-muted-foreground">-</span>}
                      </td>
                      <td className="px-3 py-1.5 font-mono text-xs" title={(t.outras_entregas || []).length ? `Outras entregas da CSU do mesmo CPF: ${t.outras_entregas.join(', ')}` : ''}>
                        {t.numero_pedido || '-'}{(t.outras_entregas || []).length > 0 && ` (+${t.outras_entregas.length})`}
                      </td>
                      <td className="px-3 py-1.5 text-xs whitespace-nowrap">{motivoAgora(t)}</td>
                      <td className={`px-3 py-1.5 text-xs ${corSituacao(t.situacao)}`} title={t.erro || t.como_achou || ''}>
                        {rotuloSituacao(t.situacao)}
                      </td>
                      <td className="px-3 py-1.5 text-xs max-w-[220px] truncate" title={[t.banco, t.fila].filter(Boolean).join(' · ')}>
                        {[t.banco, t.fila].filter(Boolean).join(' · ') || '-'}
                      </td>
                      <td className="px-3 py-1.5 text-center" title={t.conferido_por ? `Conferido por ${t.conferido_por}` : ''}>
                        <Checkbox checked={!!t.conferido} onCheckedChange={v => marcar(t, !!v)} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
